/* eslint-disable react/style-prop-object */
import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

function HorizontalScroll({
  className = '', rightSpace, children, ...props
}) {
  const scrollContainerRef = useRef(null);
  // const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isShaking, setIsShaking] = useState(false); // State to control shake animation
  const observerRef = useRef(null);

  // Update overflow status after ensuring DOM rendering is complete
  // const updateOverflowStatus = () => {
  //   const container = scrollContainerRef.current;
  //   if (container) {
  //     const canScroll = container.scrollWidth > container.clientWidth;
  //     const isScrolledToEnd = Math.ceil(container.scrollLeft + container.clientWidth) >= container.scrollWidth;
  //     setCanScrollRight(canScroll && !isScrolledToEnd);
  //   }
  // };

  const updateOverflowStatus = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const currentScroll = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;
      setCanScrollLeft(currentScroll > 0);
      setCanScrollRight(currentScroll < maxScroll - 1);
      // console.log('canscrollleft:', currentScroll, 'canScrollRight:', currentScroll, maxScroll, container.scrollWidth, container.clientWidth);
    }
  };

  // Setup ResizeObserver to monitor content changes
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      // Create ResizeObserver to monitor both container and children
      observerRef.current = new ResizeObserver(() => {
        updateOverflowStatus();
      });

      // Observe the container
      observerRef.current.observe(container);

      // Observe all direct children
      Array.from(container.children).forEach((child) => {
        observerRef.current.observe(child);
      });

      // Add scroll event listener
      container.addEventListener('scroll', updateOverflowStatus);
      window.addEventListener('resize', updateOverflowStatus);

      // Initial check with a slight delay to ensure content is rendered
      setTimeout(updateOverflowStatus, 100);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (container) {
        container.removeEventListener('scroll', updateOverflowStatus);
      }
      window.removeEventListener('resize', updateOverflowStatus);
    };
  }, [children]); // Re-run when children change

  // Scroll the container
  const handleScroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = direction === 'right' ? container.clientWidth : -container.clientWidth;
      const currentScroll = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;

      if (direction === 'right' && currentScroll < maxScroll) {
        container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        setIsShaking(false);
      } else if (direction === 'left' && currentScroll > 0) {
        container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        setIsShaking(true);
      }
      updateOverflowStatus();
    }
  };

  const combinedClassName = `overflow-x-auto custom-scroll relative ${className}`.trim();

  return (
    <div className="w-full relative">
      <div
        ref={scrollContainerRef}
        className={combinedClassName}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      >
        {children}
      </div>
      {canScrollLeft && (
        // eslint-disable-next-line jsx-a11y/control-has-associated-label
        <button
          type="button"
          onClick={() => handleScroll('left')}
          className={`absolute w-[30px] h-[30px] top-1/2 left-1 transform -translate-y-1/2 bg-white text-black px-2 py-0.5 rounded-full drop-shadow flex justify-center items-center shadow ${isShaking ? 'shake-animation' : ''}`}
          aria-label="Scroll Left"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_1439_1993)">
              <path d="M5.25 11.25H20.25C20.4489 11.25 20.6397 11.329 20.7803 11.4697C20.921 11.6103 21 11.8011 21 12C21 12.1989 20.921 12.3897 20.7803 12.5303C20.6397 12.671 20.4489 12.75 20.25 12.75H5.25C5.05109 12.75 4.86032 12.671 4.71967 12.5303C4.57902 12.3897 4.5 12.1989 4.5 12C4.5 11.8011 4.57902 11.6103 4.71967 11.4697C4.86032 11.329 5.05109 11.25 5.25 11.25Z" fill="#646464" />
              <path d="M5.56038 12L11.7809 18.219C11.9217 18.3598 12.0008 18.5508 12.0008 18.75C12.0008 18.9491 11.9217 19.1401 11.7809 19.281C11.64 19.4218 11.449 19.5009 11.2499 19.5009C11.0507 19.5009 10.8597 19.4218 10.7189 19.281L3.96888 12.531C3.89903 12.4613 3.84362 12.3785 3.80581 12.2874C3.768 12.1963 3.74854 12.0986 3.74854 12C3.74854 11.9013 3.768 11.8036 3.80581 11.7125C3.84362 11.6214 3.89903 11.5386 3.96888 11.469L10.7189 4.71897C10.8597 4.57814 11.0507 4.49902 11.2499 4.49902C11.449 4.49902 11.64 4.57814 11.7809 4.71897C11.9217 4.8598 12.0008 5.05081 12.0008 5.24997C12.0008 5.44913 11.9217 5.64014 11.7809 5.78097L5.56038 12Z" fill="#646464" />
            </g>
            <defs>
              <clipPath id="clip0_1439_1993">
                <rect width="24" height="24" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </button>
      )}
      {canScrollRight && (
        // eslint-disable-next-line jsx-a11y/control-has-associated-label
        <button
          type="button"
          onClick={() => handleScroll('right')}
          aria-label="Scroll Right"
          className={`absolute w-[30px] h-[30px] top-1/2 ${rightSpace || 'right-1'} transform -translate-y-1/2 flex justify-center items-center bg-white text-black px-2 py-0.5 rounded-full drop-shadow shadow`}
        >

          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_1439_1993)" transform="rotate(180 12 12)">
              <path d="M5.25 11.25H20.25C20.4489 11.25 20.6397 11.329 20.7803 11.4697C20.921 11.6103 21 11.8011 21 12C21 12.1989 20.921 12.3897 20.7803 12.5303C20.6397 12.671 20.4489 12.75 20.25 12.75H5.25C5.05109 12.75 4.86032 12.671 4.71967 12.5303C4.57902 12.3897 4.5 12.1989 4.5 12C4.5 11.8011 4.57902 11.6103 4.71967 11.4697C4.86032 11.329 5.05109 11.25 5.25 11.25Z" fill="#646464" />
              <path d="M5.56038 12L11.7809 18.219C11.9217 18.3598 12.0008 18.5508 12.0008 18.75C12.0008 18.9491 11.9217 19.1401 11.7809 19.281C11.64 19.4218 11.449 19.5009 11.2499 19.5009C11.0507 19.5009 10.8597 19.4218 10.7189 19.281L3.96888 12.531C3.89903 12.4613 3.84362 12.3785 3.80581 12.2874C3.768 12.1963 3.74854 12.0986 3.74854 12C3.74854 11.9013 3.768 11.8036 3.80581 11.7125C3.84362 11.6214 3.89903 11.5386 3.96888 11.469L10.7189 4.71897C10.8597 4.57814 11.0507 4.49902 11.2499 4.49902C11.449 4.49902 11.64 4.57814 11.7809 4.71897C11.9217 4.8598 12.0008 5.05081 12.0008 5.24997C12.0008 5.44913 11.9217 5.64014 11.7809 5.78097L5.56038 12Z" fill="#646464" />
            </g>
            <defs>
              <clipPath id="clip0_1439_1993">
                <rect width="24" height="24" fill="white" />
              </clipPath>
            </defs>
          </svg>

        </button>
      )}
    </div>
  );
}
HorizontalScroll.propTypes = {
  props: PropTypes.any,
  children: PropTypes.any.isRequired,
  className: PropTypes.any,
  rightSpace: PropTypes.any,
};

export default HorizontalScroll;
